import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import InitialState, {
  UpdateMarketplaceProductCategory,
} from "./marketplaceProductCategoryRedux";
import { 
  CategorySummary, 
  CategoryListResponse,
  CategoryAttributesResponse,
  AttributeConfiguration,
  MarketplaceType
} from "../../../models/MarketplaceCategory";
import { categoryClient } from "../../../../api/services/marketplaceCategoryClient";

// Enhanced initial state with new properties
interface EnhancedState extends InitialState {
  // All categories from API (flat structure with parentCategoryId relationships)
  allCategories: CategorySummary[];
  // Category attributes cache
  categoryAttributes: Record<string, AttributeConfiguration[]>;
  // Loading states
  loading: boolean;
  attributesLoading: boolean;
  // Error handling
  error: string | null;
  // Pagination for full category list
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  // Search
  searchTerm: string;
  searchResults: CategorySummary[];
  // Current marketplace
  currentMarketplace: MarketplaceType;
  // Cache timestamp
  lastFetchTime: number | null;
}

const initialState: EnhancedState = {
  marketplaceShopee: [],
  allCategories: [],
  categoryAttributes: {},
  loading: false,
  attributesLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  },
  searchTerm: '',
  searchResults: [],
  currentMarketplace: 'shopee',
  lastFetchTime: null
};

// Async thunk for fetching categories
export const fetchCategories = createAsyncThunk(
  'marketplaceProductCategory/fetchCategories',
  async ({ 
    marketplace, 
    page = 1, 
    limit = 50, 
    search 
  }: { 
    marketplace: MarketplaceType; 
    page?: number; 
    limit?: number; 
    search?: string 
  }) => {
    const response = await categoryClient.getCategories(marketplace, { page, limit, search });
    return response;
  }
);

// Async thunk for fetching category attributes
export const fetchCategoryAttributes = createAsyncThunk(
  'marketplaceProductCategory/fetchCategoryAttributes',
  async ({ 
    marketplace, 
    categoryId 
  }: { 
    marketplace: MarketplaceType; 
    categoryId: string 
  }) => {
    const response = await categoryClient.getCategoryAttributes(marketplace, categoryId);
    return { categoryId, attributes: response.data.attributes };
  }
);

// Async thunk for searching categories
export const searchCategories = createAsyncThunk(
  'marketplaceProductCategory/searchCategories',
  async ({ 
    marketplace, 
    searchTerm, 
    limit = 20 
  }: { 
    marketplace: MarketplaceType; 
    searchTerm: string; 
    limit?: number 
  }) => {
    const response = await categoryClient.searchCategories(marketplace, searchTerm, limit);
    return response;
  }
);

// Helper async thunk for fetching root categories (client-side filtering)
export const fetchRootCategories = createAsyncThunk(
  'marketplaceProductCategory/fetchRootCategories',
  async ({ 
    marketplace, 
    page = 1, 
    limit = 50, 
    search 
  }: { 
    marketplace: MarketplaceType; 
    page?: number; 
    limit?: number; 
    search?: string 
  }) => {
    const response = await categoryClient.getRootCategories(marketplace, { page, limit, search });
    return response;
  }
);

// Helper async thunk for fetching categories by parent (client-side filtering)
export const fetchCategoriesByParent = createAsyncThunk(
  'marketplaceProductCategory/fetchCategoriesByParent',
  async ({ 
    marketplace, 
    parentCategoryId, 
    page = 1, 
    limit = 50, 
    search 
  }: { 
    marketplace: MarketplaceType; 
    parentCategoryId: string; 
    page?: number; 
    limit?: number; 
    search?: string 
  }) => {
    const response = await categoryClient.getCategoriesByParent(marketplace, parentCategoryId, { page, limit, search });
    return { ...response, parentCategoryId };
  }
);

export const MarketplaceProductCategorySlice = createSlice({
  name: UpdateMarketplaceProductCategory,
  initialState: initialState,
  reducers: {
    // Keep existing reducers for backward compatibility
    setShopeeMarketplaceProductCategory: (state, action: PayloadAction<any>) => {
      state.marketplaceShopee = action.payload;
    },
    cleanReduxMarketplaceProductCategory: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.marketplaceShopee = [];
      // Also clean new state
      state.allCategories = [];
      state.categoryAttributes = {};
      state.error = null;
      state.searchResults = [];
      state.lastFetchTime = null;
    },
    // New reducers
    setCurrentMarketplace: (state, action: PayloadAction<MarketplaceType>) => {
      state.currentMarketplace = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCache: (state) => {
      state.allCategories = [];
      state.categoryAttributes = {};
      state.lastFetchTime = null;
    }
  },
  extraReducers: (builder) => {
    // Handle fetchCategories
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.allCategories = action.payload.data.categories;
        state.pagination = action.payload.pagination;
        state.lastFetchTime = Date.now();
        state.error = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch categories';
      });
    
    // Handle fetchCategoryAttributes
    builder
      .addCase(fetchCategoryAttributes.pending, (state) => {
        state.attributesLoading = true;
        state.error = null;
      })
      .addCase(fetchCategoryAttributes.fulfilled, (state, action) => {
        state.attributesLoading = false;
        state.categoryAttributes[action.payload.categoryId] = action.payload.attributes;
        state.error = null;
      })
      .addCase(fetchCategoryAttributes.rejected, (state, action) => {
        state.attributesLoading = false;
        state.error = action.error.message || 'Failed to fetch category attributes';
      });
    
    // Handle searchCategories
    builder
      .addCase(searchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.data.categories;
        state.error = null;
      })
      .addCase(searchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search categories';
      });
    
    // Handle fetchRootCategories (client-side filtering)
    builder
      .addCase(fetchRootCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRootCategories.fulfilled, (state, action) => {
        state.loading = false;
        // Store the filtered root categories in allCategories for now
        state.allCategories = action.payload.data.categories;
        state.pagination = action.payload.pagination;
        state.lastFetchTime = Date.now();
        state.error = null;
      })
      .addCase(fetchRootCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch root categories';
      });
    
    // Handle fetchCategoriesByParent (client-side filtering)
    builder
      .addCase(fetchCategoriesByParent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoriesByParent.fulfilled, (state, action) => {
        state.loading = false;
        // Store the filtered children categories in allCategories for now
        state.allCategories = action.payload.data.categories;
        state.error = null;
      })
      .addCase(fetchCategoriesByParent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch category children';
      });
  },
});

// Action creators are generated for each case reducer function
export const {
  setShopeeMarketplaceProductCategory,
  cleanReduxMarketplaceProductCategory,
  setCurrentMarketplace,
  setSearchTerm,
  clearError,
  clearCache
} = MarketplaceProductCategorySlice.actions;

// Selectors
export const selectCategories = (state: any) => state.MarketplaceProductCategory.allCategories;
export const selectRootCategories = (state: any) => 
  state.MarketplaceProductCategory.allCategories.filter((cat: CategorySummary) => !cat.parentCategoryId);
export const selectCategoryChildren = (state: any, parentId: string) => 
  state.MarketplaceProductCategory.allCategories.filter((cat: CategorySummary) => cat.parentCategoryId === parentId);
export const selectCategoryAttributes = (state: any, categoryId: string) => 
  state.MarketplaceProductCategory.categoryAttributes[categoryId];
export const selectLoading = (state: any) => state.MarketplaceProductCategory.loading;
export const selectRootLoading = (state: any) => state.MarketplaceProductCategory.loading;
export const selectChildrenLoading = (state: any, parentId: string) => false; // No longer needed with client-side filtering
export const selectError = (state: any) => state.MarketplaceProductCategory.error;
export const selectPagination = (state: any) => state.MarketplaceProductCategory.pagination;
export const selectSearchResults = (state: any) => state.MarketplaceProductCategory.searchResults;
export const selectCurrentMarketplace = (state: any) => state.MarketplaceProductCategory.currentMarketplace;

// You must export the reducer as follows for it to be able to be read by the store.
export default MarketplaceProductCategorySlice.reducer;
